import { Button } from '@cian/ui-kit/button';
import { Input } from '@cian/ui-kit/input';
import { IconActionArrowRight16, IconActionFilters16, IconActionSearch16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';

import * as styles from './DesktopSearchBlockInput.css';
import { Experiment } from '@cian/experiments/ab';
import { BX_DESCKTOP_MAIN_PAGE } from 'shared/constants/experiments';
import { dispatchFilterClickEvent } from 'shared/utils/filterClickEvent';
import { trackInputInFocused } from 'shared/containers/DesktopSearchInputContainer/tracking';

interface IDesktopSearchBlockInputProps {
  query: string;
  loading: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onQueryChange(query: string): void;
  onSubmit(isMap: boolean): void;
  onChangeFocus(isFocused: boolean): void;
}

export const DesktopSearchBlockInput = React.forwardRef<HTMLFormElement, IDesktopSearchBlockInputProps>(
  function DesktopSearchBlockInput({ query, loading, inputRef, onQueryChange, onSubmit, onChangeFocus }, ref) {
    const [isFocused, setIsFocused] = React.useState(false);

    React.useEffect(() => {
      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          setIsFocused(false);
        }
      };

      window.addEventListener('pageshow', handlePageShow);

      return () => {
        window.removeEventListener('pageshow', handlePageShow);
      };
    }, []);

    const handleInputChange = React.useCallback(
      (_: React.ChangeEvent<HTMLInputElement>, value: string) => onQueryChange(value),
      [onQueryChange],
    );

    const handleFocusChange = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const newValue = !isFocused;
        const name = e.relatedTarget && (e.relatedTarget as HTMLElement).getAttribute('data-name');

        onChangeFocus(newValue);

        if (loading || (name !== 'search-submit' && name !== 'map-search-submit')) {
          setIsFocused(newValue);

          if (newValue) {
            trackInputInFocused();
          }
        }
      },
      [isFocused, loading, onChangeFocus],
    );

    const handleSubmit = React.useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) {
          return;
        }

        onSubmit(false);
      },
      [loading, onSubmit],
    );

    const handleMapClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (loading) {
          return;
        }

        onSubmit(true);
      },
      [loading, onSubmit],
    );

    const handleFiltersClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (loading) {
          return;
        }

        dispatchFilterClickEvent();
      },
      [loading, onSubmit],
    );

    return (
      <form ref={ref} className={styles['form']} action="#" onSubmit={handleSubmit}>
        <IconActionSearch16 color="icon-primary-default" />
        <Input
          onFocus={handleFocusChange}
          onBlur={handleFocusChange}
          inputRef={inputRef}
          placeholder="Купить квартиру с большой кухней рядом с метро"
          size="M"
          value={query}
          onChange={handleInputChange}
          data-testid="desktop-search-input"
        />
        <div className={styles['buttons']}>
          <div className={styles['button']}>
            {/* Кнопка Найти всегда показывается в группе B */}
            <Experiment name={BX_DESCKTOP_MAIN_PAGE} group="B">
              <Button
                data-name="search-submit"
                data-testid="desktop-search-button"
                size="XS"
                title="Найти"
                theme="fill_primary"
                loading={loading}
                beforeIcon={<IconActionArrowRight16 color={'icon-inverted-default'} />}
              />
            </Experiment>

            {/* Кнопка Найти всегда показывается когда строка в фокусе в группе С */}
            <Experiment name={BX_DESCKTOP_MAIN_PAGE} group="C">
              {isFocused && (
                <Button
                  data-name="search-submit"
                  data-testid="desktop-search-button"
                  size="XS"
                  title="Найти"
                  theme="fill_primary"
                  loading={loading}
                  beforeIcon={<IconActionArrowRight16 color={'icon-inverted-default'} />}
                />
              )}
            </Experiment>

            {/* Кнопка Фильтры всегда показывается в группе С */}
            <Experiment name={BX_DESCKTOP_MAIN_PAGE} group="C">
              {!isFocused && (
                <Button
                  data-name="filters"
                  data-testid="desktop-filters-button"
                  size="XS"
                  title="Фильтры"
                  theme="fill_secondary"
                  beforeIcon={<IconActionFilters16 color={'icon-main-default'} />}
                  onClick={handleFiltersClick}
                />
              )}
            </Experiment>
          </div>
          {/* Кнопка На карте показывается когда в строке есть текст или она в фокусе в группе B и С */}
          <Experiment name={BX_DESCKTOP_MAIN_PAGE} group="B">
            {(query || isFocused) && (
              <div className={styles['button-map']}>
                <Button
                  data-name="map-search-submit"
                  data-testid="desktop-map-search-button"
                  size="XS"
                  title="Найти на карте"
                  theme="fill_secondary"
                  disabled={loading}
                  onClick={handleMapClick}
                >
                  На карте
                </Button>
              </div>
            )}
          </Experiment>

          <Experiment name={BX_DESCKTOP_MAIN_PAGE} group="C">
            {(query || isFocused) && (
              <div className={styles['button-map']}>
                <Button
                  data-name="map-search-submit"
                  data-testid="desktop-map-search-button"
                  size="XS"
                  title="Найти на карте"
                  theme="fill_secondary"
                  disabled={loading}
                  onClick={handleMapClick}
                >
                  На карте
                </Button>
              </div>
            )}
          </Experiment>
        </div>
      </form>
    );
  },
);
